
/**
 * Custom Photoswipe UI Class.
 *
 * Completely replaces the default Photoswipe UI class.
 *
 * This allows us to add custom buttons, functionality etc. and
 * get rid of all the photoswipe standard stuff noone needs.
 */
export default class PhotoswipeUI {


    /**
     *
     */
    constructor(photoswipe, framework) {
        this.photoswipe = photoswipe;
        this.framework = framework;
        this.options = photoswipe.options;

        this.defaultUIOptions = {
            clickToCloseNonZoomable: false,
            tabToToggleUi: true,
            closeElClasses: ['pswp__zoom-wrap', 'pswp__item', 'photoswipeUI-closeButton'],
        };
    }


    /**
     * Init hook.
     */
    init() {

        // Register UI
        this.framework.extend(this.photoswipe.options, this.defaultUIOptions, true);

        this.uiEl = this.photoswipe.scrollWrap.querySelector('.photoswipeUI');
        this.captionEl = this.uiEl.querySelector('.photoswipeUI-captions');
        this.counterEl = this.uiEl.querySelector('.photoswipeUI-counterInner');
        this.arrowLeft = this.photoswipe.scrollWrap.querySelector('.photoswipeUI-arrowLeft');
        this.arrowRight = this.photoswipe.scrollWrap.querySelector('.photoswipeUI-arrowRight');

        this.photoswipe?.listen('doubleTap', this.zoomImage.bind(this)); // Zoom on Double Tab
        this.photoswipe?.listen('beforeChange', this.update.bind(this)); // Update UI when going to next image
        this.photoswipe?.listen('parseVerticalMargin', this.adjustTopAndBottomSpace.bind(this)); // Calculate space for captions

        // On single tap (touchscreen)
        const onGlobalTapEvent = this.onGlobalTap.bind(this);

        // Bind tap event
        this.photoswipe.listen('bindEvents', () => {
            this.framework.bind(this.photoswipe.scrollWrap, 'pswpTap', onGlobalTapEvent);
        });

        this.photoswipe.listen('unbindEvents', () => {
            this.framework.unbind(this.photoswipe.scrollWrap, 'pswpTap', onGlobalTapEvent);
        });

        this.arrowLeft.addEventListener('click', this.photoswipe.prev);
        this.arrowRight.addEventListener('click', this.photoswipe.next);
    }


    /**
     * Update captions and counter when going to next/prev image
     */
    update() {
        this.updateCaptions();
        this.updateCounter();
    }


    /**
     * When tap (mouseclick and tap!)
     */
    onGlobalTap(e) {
        const target = e.target;

        console.log('onGlobalTag(e)');

        // close gallery if clicked outside of the image
        if (this.hasCloseClass(target)) {
            this.photoswipe.close();
            return;
        }

        // When using mouse click, we zoom in or zoom out the image
        if (e.detail && e.detail.pointerType === 'mouse') {
            if (target.classList.contains('pswp__img')) {
                if (this.photoswipe.getZoomLevel() === 1 && this.photoswipe.getZoomLevel() <= this.photoswipe.currItem.fitRatio) {
                    if (this.options.clickToCloseNonZoomable) {
                        this.photoswipe.close();
                    }
                } else {
                    this.photoswipe.toggleDesktopZoom(e.detail.releasePoint);

                    if (this.photoswipe.getZoomLevel() === 1) {
                        this.uiEl.classList.remove('photoswipeUI-isHidden');
                    } else {
                        this.uiEl.classList.add('photoswipeUI-isHidden');
                    }
                }
            }
        } else if (this.options.tabToToggleUi) {
            this.uiEl.classList.toggle('photoswipeUI-isHidden');
        }
    }


    /**
     * Zoom Image (this one is only for touchscreens)
     */
    zoomImage(point) {
        const initialZoomLevel = this.photoswipe.currItem.initialZoomLevel;
        if (this.photoswipe.getZoomLevel() !== initialZoomLevel) {
            this.photoswipe.zoomTo(initialZoomLevel, point, 333);
            this.uiEl.classList.remove('photoswipeUI-isHidden');
        } else {
            this.photoswipe.zoomTo(this.options.getDoubleTapZoom(false, this.photoswipe.currItem), point, 333);
            this.uiEl.classList.add('photoswipeUI-isHidden');
        }
    }


    /**
     * Update caption text
     */
    updateCaptions() {
        this.captionEl.innerHTML = this.photoswipe.currItem.captions;
    }


    /**
     * Update image counter
     */
    updateCounter() {
        const currentCount = this.photoswipe.getCurrentIndex() + 1;
        const totalCount = this.options.getNumItemsFn();

        if (totalCount > 1) {
            this.counterEl.innerHTML = `${currentCount}/${totalCount}`;
        } else {
            this.counterEl.innerHTML = '';
        }

    }


    /**
     * Define how much space should be at the bottom to leave space for the captions.
     * This actually defines the height of the image.
     */
    adjustTopAndBottomSpace(item) {

        const gap = item.vGap;
        const screenHeight = window.innerHeight;
        const minBottom = screenHeight / 10;
        const minTop = screenHeight / 20;
        let spaceBottom = minBottom;

        // Only if there are captions:
        // Create fake caption element, add the text to it and get the height of it.
        // This way, the bottom space is always as high as the text is.
        // If there is no captions. use minBottom & minTop (minimal space)
        if (item.captions.length > 0) {
            const fakeCaptionsEl = document.createElement('div');
            fakeCaptionsEl.classList.add('photoswipeUI-fakeCaptions');
            this.captionEl.appendChild(fakeCaptionsEl);
            fakeCaptionsEl.innerHTML = item.captions;
            spaceBottom = fakeCaptionsEl.clientHeight;
            const margin = window.getComputedStyle(this.captionEl).getPropertyValue('margin-top').replace('px', '');
            spaceBottom += 2 * margin;
            fakeCaptionsEl.parentNode.removeChild(fakeCaptionsEl);
        }

        gap.top = minTop;
        gap.bottom = spaceBottom;

    }


    /**
     * Returns true if the clicked element has a "close" class on it (means its meant to close the modal on click)
     */
    hasCloseClass(target) {
        for (const closeElClass of this.options.closeElClasses) {
            if (target.classList.contains(closeElClass)) {
                return true;
            }
        }
        return false;
    }

}
